import React, { Suspense } from "react"
import { createBrowserRouter, Outlet, useLocation } from "react-router-dom"
import { Loader2 } from "lucide-react"
import { AuthProvider } from "@/contexts/auth-context"
import { OrganizationProvider } from "@/contexts/organization-context"
import { Toaster } from "@/components/ui/toaster"
import { RouteError } from "./route-error"
import { importRouteModule } from "@/lib/route-module-recovery"

/** Wrap a lazily-imported (default-export) page/layout in a Suspense boundary. */
function load(factory: () => Promise<{ default: React.ComponentType<any> }>) {
  const C = React.lazy(() => importRouteModule(factory))
  return (
    <Suspense fallback={<FullPageSpinner />}>
      <C />
    </Suspense>
  )
}

function FullPageSpinner() {
  return (
    <div className="flex h-screen items-center justify-center bg-background">
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
    </div>
  )
}

/** Root element: provides auth context (inside the router so useNavigate works) + global chrome. */
function RootProviders() {
  const location = useLocation();
  if (location.pathname.startsWith("/questionnaire/forms/")) return <Outlet />;
  return (
    <AuthProvider>
      <OrganizationProvider>
        <Outlet />
        <Toaster />
      </OrganizationProvider>
    </AuthProvider>
  )
}

export const router = createBrowserRouter([
  {
    element: <RootProviders />,
    errorElement: <RouteError />,
    children: [
      { index: true, element: load(() => import("@/app/page")) },
      { path: "login", element: load(() => import("@/app/login/page")) },
      { path: "reset-password", element: load(() => import("@/app/reset-password/page")) },

      // Platform admin
      {
        path: "admin",
        element: load(() => import("@/app/admin/layout")),
        children: [
          { index: true, element: load(() => import("@/app/admin/page")) },
          { path: "login", element: load(() => import("@/app/admin/login/page")) },
          { path: "dashboard", element: load(() => import("@/app/admin/dashboard/page")) },
          { path: "permissions", element: load(() => import("@/app/admin/permissions/page")) },
          { path: "system-logs", element: load(() => import("@/app/admin/system-logs/page")) },
          { path: "users-management", element: load(() => import("@/app/admin/users-management/page")) },
        ],
      },

      // Candidate auth
      {
        path: "candidate",
        children: [
          { index: true, element: load(() => import("@/app/candidate/page")) },
          { path: "login", element: load(() => import("@/app/candidate/login/page")) },
          { path: "confirm", element: load(() => import("@/app/candidate/confirm/page")) },
          { path: "signup", element: load(() => import("@/app/candidate/signup/page")) },
        ],
      },

      // Postulate app
      {
        path: "postulate",
        children: [
          { index: true, element: load(() => import("@/app/postulate/page")) },
          { path: "vacancies/:id", element: load(() => import("@/app/postulate/vacancies/[id]/page")) },
          {
            path: "admin",
            element: load(() => import("@/app/postulate/admin/layout")),
            children: [
              { path: "applications", element: load(() => import("@/app/postulate/admin/applications/page")) },
              { path: "applications/:id", element: load(() => import("@/app/postulate/admin/applications/[id]/page")) },
              { path: "candidates", element: load(() => import("@/app/postulate/admin/candidates/page")) },
              { path: "candidates/:id", element: load(() => import("@/app/postulate/admin/candidates/[id]/page")) },
              { path: "catalog-types", element: load(() => import("@/app/postulate/admin/catalog-types/page")) },
              { path: "catalogs", element: load(() => import("@/app/postulate/admin/catalogs/page")) },
              { path: "dashboard", element: load(() => import("@/app/postulate/admin/dashboard/page")) },
              { path: "matching-config", element: load(() => import("@/app/postulate/admin/matching-config/page")) },
              { path: "notifications", element: load(() => import("@/app/postulate/admin/notifications/page")) },
              { path: "organizations", element: load(() => import("@/app/postulate/admin/organizations/page")) },
              { path: "organizations/:id", element: load(() => import("@/app/postulate/admin/organizations/[id]/page")) },
              { path: "permissions", element: load(() => import("@/app/postulate/admin/permissions/page")) },
              { path: "pipeline", element: load(() => import("@/app/postulate/admin/pipeline/page")) },
              { path: "pipeline/vacancies/:id", element: load(() => import("@/app/postulate/admin/pipeline/vacancies/[id]/page")) },
              { path: "rate-candidates", element: load(() => import("@/app/postulate/admin/rate-candidates/page")) },
              { path: "rating-config", element: load(() => import("@/app/postulate/admin/rating-config/page")) },
              { path: "rating-stale-config", element: load(() => import("@/app/postulate/admin/rating-stale-config/page")) },
              { path: "reports", element: load(() => import("@/app/postulate/admin/reports/page")) },
              { path: "services", element: load(() => import("@/app/postulate/admin/services/page")) },
              { path: "settings", element: load(() => import("@/app/postulate/admin/settings/page")) },
              { path: "templates", element: load(() => import("@/app/postulate/admin/templates/page")) },
              { path: "users", element: load(() => import("@/app/postulate/admin/users/page")) },
              { path: "vacancies", element: load(() => import("@/app/postulate/admin/vacancies/page")) },
              { path: "vacancies/:id", element: load(() => import("@/app/postulate/admin/vacancies/[id]/page")) },
            ],
          },
          {
            path: "candidate",
            element: load(() => import("@/app/postulate/candidate/layout")),
            children: [
              { path: "applications", element: load(() => import("@/app/postulate/candidate/applications/page")) },
              { path: "applications/:id", element: load(() => import("@/app/postulate/candidate/applications/[id]/page")) },
              { path: "dashboard", element: load(() => import("@/app/postulate/candidate/dashboard/page")) },
              { path: "profile", element: load(() => import("@/app/postulate/candidate/profile/page")) },
              { path: "profile/complete_profile", element: load(() => import("@/app/postulate/candidate/profile/complete_profile/page")) },
              { path: "vacancies", element: load(() => import("@/app/postulate/candidate/vacancies/page")) },
              { path: "vacancies/:id", element: load(() => import("@/app/postulate/candidate/vacancies/[id]/page")) },
            ],
          },
        ],
      },

      // Questionnaire app
      {
        path: "questionnaire",
        children: [
          { index: true, element: load(() => import("@/app/questionnaire/page")) },
          { path: "login", element: load(() => import("@/app/questionnaire/login/page")) },
          { path: "forms/:token", element: load(() => import("@/components/registration/public-form")) },
          {
            path: "admin",
            element: load(() => import("@/app/questionnaire/admin/layout")),
            children: [
              { index: true, element: load(() => import("@/app/questionnaire/admin/page")) },
              { path: "questionnaires", element: load(() => import("@/app/questionnaire/admin/questionnaires/page")) },
              { path: "forms/new", element: load(() => import("@/components/registration/admin-form")) },
              { path: "forms/:id", element: load(() => import("@/components/registration/admin-form")) },
              { path: "questionnaires/new", element: load(() => import("@/app/questionnaire/admin/questionnaires/new/page")) },
              { path: "questionnaires/:id/setup", element: load(() => import("@/app/questionnaire/admin/questionnaires/[id]/setup/page")) },
              { path: "questionnaires/:id", element: load(() => import("@/app/questionnaire/admin/questionnaires/[id]/page")) },
              { path: "questionnaires/:id/analytics", element: load(() => import("@/app/questionnaire/admin/questionnaires/[id]/analytics/page")) },
              { path: "questionnaires/:id/bulk-evaluation", element: load(() => import("@/app/questionnaire/admin/questionnaires/[id]/bulk-evaluation/page")) },
            ],
          },
          {
            path: "evaluation",
            element: load(() => import("@/app/questionnaire/evaluation/layout")),
            children: [
              { index: true, element: load(() => import("@/app/questionnaire/evaluation/page")) },
              { path: ":accessCode", element: load(() => import("@/app/questionnaire/evaluation/[accessCode]/page")) },
              { path: ":accessCode/bulk", element: load(() => import("@/app/questionnaire/evaluation/[accessCode]/bulk/page")) },
              { path: ":accessCode/instrucciones", element: load(() => import("@/app/questionnaire/evaluation/[accessCode]/instrucciones/page")) },
            ],
          },
          {
            path: "super-admin",
            element: load(() => import("@/app/questionnaire/super-admin/layout")),
            children: [
              { path: "assessment-templates", element: load(() => import("@/app/questionnaire/super-admin/assessment-templates/page")) },
              { path: "categories-indexes", element: load(() => import("@/app/questionnaire/super-admin/categories-indexes/page")) },
              { path: "clima-templates", element: load(() => import("@/app/questionnaire/super-admin/clima-templates/page")) },
              { path: "clima-templates/noms", element: load(() => import("@/app/questionnaire/super-admin/clima-templates/noms/page")) },
              { path: "client-configs", element: load(() => import("@/app/questionnaire/super-admin/client-configs/page")) },
              { path: "clients", element: load(() => import("@/app/questionnaire/super-admin/clients/page")) },
              { path: "examen-templates", element: load(() => import("@/app/questionnaire/super-admin/examen-templates/page")) },
              { path: "participant-hierarchy-roles", element: load(() => import("@/app/questionnaire/super-admin/participant-hierarchy-roles/page")) },
              { path: "question-pool", element: load(() => import("@/app/questionnaire/super-admin/question-pool/page")) },
              { path: "types", element: load(() => import("@/app/questionnaire/super-admin/types/page")) },
              { path: "types/:id", element: load(() => import("@/app/questionnaire/super-admin/types/[id]/page")) },
              {
                path: "ai-usage",
                element: load(() => import("@/app/questionnaire/super-admin/ai-usage/layout")),
                children: [
                  { index: true, element: load(() => import("@/app/questionnaire/super-admin/ai-usage/page")) },
                ],
              },
            ],
          },
        ],
      },

      // Catch-all 404
      { path: "*", element: <RouteError notFound /> },
    ],
  },
])
